import React from "react";
import { Reveal } from "../components/Reveal";
import { PageHero, CtaBand, SectionHead } from "../components/UI";
import {
  IconMic,
  IconUpload,
  IconText,
  IconUsers,
  IconBrain,
  IconDoc,
  IconTask,
  IconFingerprint,
  IconSearch,
  IconShield,
  IconCheck,
  IconClock,
  IconLayers,
} from "../components/Icons";

const groups = [
  {
    eyebrow: "1 · Tiếp nhận âm thanh",
    title: "Ghi âm trực tiếp hoặc tải file có sẵn",
    desc: "Hai cách đưa dữ liệu vào hệ thống, phù hợp cả họp trực tiếp lẫn xử lý bản ghi cũ.",
    items: [
      [<IconMic size={22} />, "Ghi âm trực tiếp", "Ghi nhận nội dung ngay trên trình duyệt, đánh dấu các đoạn quan trọng trong lúc họp."],
      [<IconUpload size={22} />, "Tải lên file", "Xử lý các tệp MP3, WAV, M4A có sẵn để tự động tạo bản ghi văn bản."],
    ],
  },
  {
    eyebrow: "2 · Transcript & hiệu chỉnh",
    title: "Bản phiên âm gắn người nói, dễ rà soát",
    desc: "Văn bản theo từng mốc thời gian, phân biệt người nói và chỉnh sửa thuận tiện.",
    items: [
      [<IconText size={22} />, "Phiên âm theo thời gian", "Tách lời nói thành câu, gắn mốc thời gian để nghe lại đúng đoạn."],
      [<IconUsers size={22} />, "Gán & quản lý người nói", "Đặt tên cho từng người, tách đoạn, gán lại khi nhận diện chưa đúng."],
    ],
  },
  {
    eyebrow: "3 · Phân tích AI",
    title: "Nắm trọng tâm mà không cần đọc lại toàn bộ",
    desc: "AI tổng hợp nội dung, nhận diện chủ đề, quyết định và nhiệm vụ.",
    items: [
      [<IconBrain size={22} />, "Tóm tắt & ý chính", "Rút gọn nội dung, nhận diện chủ đề và dòng thời gian thảo luận."],
      [<IconTask size={22} />, "Trích nhiệm vụ", "Bóc tách công việc, đơn vị/cá nhân phụ trách và thời hạn hoàn thành."],
    ],
  },
  {
    eyebrow: "4 · Biên bản & báo cáo",
    title: "Dự thảo kết luận sẵn sàng để trình ký",
    desc: "Sinh văn bản theo bố cục hành chính chuẩn và xuất ra định dạng quen thuộc.",
    items: [
      [<IconDoc size={22} />, "Dự thảo theo mẫu", "Bối cảnh, nội dung thảo luận, kết luận của chủ trì và nhiệm vụ."],
      [<IconLayers size={22} />, "Xuất Word / PDF", "Tải xuống nhanh theo mẫu có sẵn để lưu trữ, trình ký hoặc chia sẻ."],
    ],
  },
  {
    eyebrow: "5 · Hồ sơ giọng nói",
    title: "Nhận diện người phát biểu qua các cuộc họp",
    desc: "Quản lý dấu giọng của từng cá nhân để transcript rõ ràng hơn theo thời gian.",
    items: [
      [<IconFingerprint size={22} />, "Hồ sơ giọng nói", "Tạo dấu giọng cho từng người, tự nhận diện trong cuộc họp sau."],
      [<IconUsers size={22} />, "Tổng hợp theo người", "Thuận tiện khi cần gom ý kiến theo từng cá nhân tham gia."],
    ],
  },
  {
    eyebrow: "6 · Nhật ký & quản trị",
    title: "Kiểm soát dữ liệu và truy vết thao tác",
    desc: "Phân quyền, nhật ký kiểm toán và quản trị người dùng cho khối quản lý.",
    items: [
      [<IconSearch size={22} />, "Nhật ký kiểm toán", "Ghi nhận thao tác xem, sửa, tải xuống hoặc xoá dữ liệu."],
      [<IconShield size={22} />, "Quản trị & phân quyền", "Quản lý người dùng, phân quyền và theo dõi dữ liệu cuộc họp."],
    ],
  },
];

export function Features() {
  return (
    <>
      <PageHero
        eyebrow="Tính năng"
        title="Toàn bộ vòng đời một cuộc họp, trong một sản phẩm"
        sub="Từ ghi âm, phiên âm, phân tích đến biên bản và quản trị — sáu nhóm chức năng phối hợp liền mạch."
      />

      {groups.map((g, gi) => (
        <section className={`section ${gi % 2 === 1 ? "soft" : ""}`} key={gi}>
          <div className="container">
            <SectionHead eyebrow={g.eyebrow} title={g.title} sub={g.desc} />
            <div className="grid cards-2">
              {g.items.map(([ic, h, p], i) => (
                <Reveal className="card" key={i} delay={i * 70}>
                  <div className={`ic ${["", "teal", "violet", "amber", "teal", ""][gi]}`}>
                    {ic as React.ReactNode}
                  </div>
                  <h3>{h as string}</h3>
                  <p>{p as string}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="section dark">
        <div className="container">
          <SectionHead center eyebrow="Khác biệt" title="Vì sao Ghi Âm Pro không chỉ là phần mềm ghi âm" />
          <div className="grid cards-3">
            {[
              ["Tập trung nghiệp vụ họp", "Không dừng ở ghi âm — đi đến tận biên bản, kết luận và nhiệm vụ sau họp."],
              ["Tối ưu quy trình văn phòng", "Có mẫu báo cáo, xuất Word/PDF và rà soát trước khi phát hành."],
              ["Quản trị & truy vết", "Nhật ký thao tác và phân quyền phù hợp nhu cầu quản lý nội bộ."],
            ].map(([h, p], i) => (
              <Reveal
                key={i}
                className="card"
                delay={i * 80}
                style={{ background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.12)" }}
              >
                <div className="ic" style={{ background: "rgba(255,255,255,0.1)", color: "#8af0ff" }}>
                  <IconCheck size={22} />
                </div>
                <h3 style={{ color: "#fff" }}>{h}</h3>
                <p style={{ color: "#aebbe6" }}>{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
